/**
 * Tournament Games API service (epic 2, Slice 1).
 *
 * Reads a tournament's games and sets a single game's coach assignments.
 * Multi-tenancy is enforced server-side via the Firebase token +
 * X-Organization-Id header injected by the shared fetch client.
 */

import { get, post, put, del } from "./api";
import type {
  AssociateTournamentTeamRequest,
  AutoAssignRequest,
  AutoAssignResponse,
  GameAssigneesEntry,
  GamePitchersEntry,
  ResolveGameConflictRequest,
  SetGameAssigneesRequest,
  SetGamesAssigneesRequest,
  SetGamesAssigneesResponse,
  SetGamesPitchersRequest,
  SetGamesPitchersResponse,
  SetVenueLocationRequest,
  TournamentGameDto,
  TournamentGamesResponse,
  UpdateTournamentGameRequest,
} from "../types/tournament-games";

/**
 * Fetch the games under a tournament, each with its own coaches (`assignees`)
 * and the deduped tournament ∪ game coverage (`effectiveCoaches`).
 */
export const getTournamentGames = async (
  calendarEventId: string,
): Promise<TournamentGamesResponse> => {
  return get<TournamentGamesResponse>(`/api/tournaments/${calendarEventId}/games`);
};

/**
 * Edit a single game (fix an LLM mis-parse). Send the game's current `updatedAt` as
 * `expectedUpdatedAt`; a stale value gets a 409. Changing `fieldName` re-resolves the venue
 * server-side. Returns the refreshed games list.
 */
export const updateGame = async (
  calendarEventId: string,
  gameId: string,
  body: UpdateTournamentGameRequest,
): Promise<TournamentGamesResponse> => {
  return put<TournamentGamesResponse>(
    `/api/tournaments/${calendarEventId}/games/${gameId}`,
    body,
  );
};

/** Remove a single game (e.g. a phantom the importer added). Returns the refreshed games list. */
export const deleteGame = async (
  calendarEventId: string,
  gameId: string,
): Promise<TournamentGamesResponse> => {
  return del<TournamentGamesResponse>(`/api/tournaments/${calendarEventId}/games/${gameId}`);
};

/**
 * Full-replace a single game's own coaches. Passing an empty array clears them
 * (the game then inherits tournament-only coverage). Foreign or inactive ids
 * are dropped server-side. Returns the updated game DTO (with a refreshed
 * `effectiveCoaches`).
 */
export const setGameAssignees = async (
  calendarEventId: string,
  gameId: string,
  assigneeUserIds: string[],
): Promise<TournamentGameDto> => {
  const body: SetGameAssigneesRequest = { assigneeUserIds };
  return put<TournamentGameDto>(
    `/api/tournaments/${calendarEventId}/games/${gameId}/assignees`,
    body,
  );
};

/**
 * Full-replace the coaches of several games in one request. Games left out of
 * `games` keep whatever they had. Returns the refreshed games list plus any ids
 * the server did not recognise (a stale grid entry is skipped, not fatal).
 *
 * Prefer this over looping `setGameAssignees` when saving more than one game:
 * the server groups its "assigned to you" push per coach per request, so a loop
 * would send a coach one notification per game.
 */
export const setGamesAssignees = async (
  calendarEventId: string,
  games: GameAssigneesEntry[],
): Promise<SetGamesAssigneesResponse> => {
  const body: SetGamesAssigneesRequest = { games };
  return put<SetGamesAssigneesResponse>(
    `/api/tournaments/${calendarEventId}/games/assignees`,
    body,
  );
};

/**
 * Full-replace the pitchers (org prospects) of several games in one request. Games left out of
 * `games` keep whatever they had; an empty `prospectIds` clears that game. Returns the refreshed
 * games list plus any ids the server did not recognise.
 */
export const setGamesPitchers = async (
  calendarEventId: string,
  games: GamePitchersEntry[],
): Promise<SetGamesPitchersResponse> => {
  const body: SetGamesPitchersRequest = { games };
  return put<SetGamesPitchersResponse>(
    `/api/tournaments/${calendarEventId}/games/pitchers`,
    body,
  );
};

/**
 * Ask the server for a balanced, conflict-aware coach assignment across a
 * tournament's games (only `gameIds` when given). Nothing is persisted — the
 * returned proposal is applied to the assignment grid for tweak-before-save.
 * Foreign/inactive coach ids are dropped server-side; an empty/all-invalid pool
 * is a 400. An older API ignores `gameIds`, so callers still scope the result.
 * `draftAssignees` carries the grid's unsaved coaches on games outside `gameIds`.
 */
export const autoAssignTournament = async (
  calendarEventId: string,
  coachUserIds: string[],
  gameIds?: string[],
  draftAssignees?: Record<string, string[]>,
): Promise<AutoAssignResponse> => {
  const body: AutoAssignRequest = {
    coachUserIds,
    ...(gameIds ? { gameIds } : {}),
    ...(gameIds && draftAssignees ? { draftAssignees } : {}),
  };
  return post<AutoAssignResponse>(
    `/api/tournaments/${calendarEventId}/auto-assign`,
    body,
  );
};

/**
 * Publish a draft tournament (created by the Build Itinerary wizard) so it lands
 * on the calendar. Called when the wizard finishes. No-op server-side if already
 * published; 404 if the id isn't a tournament in the caller's org.
 */
export const publishTournament = async (
  calendarEventId: string,
  title?: string,
): Promise<void> => {
  const trimmed = title?.trim();
  await put<void>(
    `/api/tournaments/${calendarEventId}/publish`,
    trimmed ? { title: trimmed } : {},
  );
};

/**
 * Finish setting up a distributed schedule copy. Clears the copy's `needsSetup`
 * flag server-side (org-scoped) so it stops prompting for setup and settles onto
 * the recipient's calendar. Called when the wizard finishes in setup mode
 * (instead of publishTournament, which is for the owner's draft). 404 if the id
 * isn't a tournament in the caller's org.
 */
export const completeTournamentSetup = async (
  calendarEventId: string,
): Promise<void> => {
  await post<void>(`/api/tournaments/${calendarEventId}/complete-setup`, {});
};

/** Result of auto-resolving a tournament's teams: how many matched vs were created. */
export interface AutoResolveTeamsResponse {
  games: TournamentGamesResponse;
  matchedExisting: number;
  /** Always 0 now — auto-resolve no longer creates teams (kept for wire-compat). */
  created: number;
  /** Raw names with no rostered match; left unlinked (raw name kept on the game). */
  unmatched: number;
}

/**
 * Auto match-or-create every currently-unlinked team in a tournament against the
 * caller's org, then link the games — one atomic, idempotent call. Used on the
 * recipient's Match Teams step so a distributed copy (team FKs stripped on
 * distribution) starts fully linked instead of "0 linked". A no-op (0/0) when
 * everything is already linked. 404 if the id isn't a tournament in the org.
 */
export const autoResolveTeams = async (
  calendarEventId: string,
): Promise<AutoResolveTeamsResponse> => {
  return post<AutoResolveTeamsResponse>(
    `/api/tournaments/${calendarEventId}/auto-resolve-teams`,
    {},
  );
};

/**
 * Associate an imported team (by its raw extracted name) with an existing org
 * team, re-pointing every game that names it. Returns the refreshed games so the
 * caller can show updated matched players. 400 if the team is unknown/foreign.
 */
export const associateTournamentTeam = async (
  calendarEventId: string,
  rawName: string,
  teamId: string,
): Promise<TournamentGamesResponse> => {
  const body: AssociateTournamentTeamRequest = { rawName, teamId };
  return put<TournamentGamesResponse>(
    `/api/tournaments/${calendarEventId}/team-associations`,
    body,
  );
};

/**
 * Resolve an active update conflict on a locally-edited game (see the merge
 * conflict lifecycle). `keepMine` keeps the local edit and just clears the
 * conflict flag; `takeTheirs` applies the captured incoming source values and
 * clears the edit/conflict flags. Returns the refreshed game.
 *
 * Note the route base is `api/tournaments` (not the schedule-ingestions base).
 */
export const resolveGameConflict = async (
  calendarEventId: string,
  gameId: string,
  action: "keepMine" | "takeTheirs",
  signal?: AbortSignal,
): Promise<TournamentGameDto> => {
  const body: ResolveGameConflictRequest = { action };
  return post<TournamentGameDto>(
    `/api/tournaments/${calendarEventId}/games/${gameId}/resolve-conflict`,
    body,
    { signal },
  );
};

/**
 * Correct a venue's physical location across every game at that venue. Pass the picked Google
 * `placeId` to resolve the exact place server-side. Returns the refreshed games list.
 */
export const setVenueLocation = async (
  calendarEventId: string,
  body: SetVenueLocationRequest,
): Promise<TournamentGamesResponse> => {
  return put<TournamentGamesResponse>(
    `/api/tournaments/${calendarEventId}/venue`,
    body,
  );
};
